import { createV3Queries } from "@query/utils/createV3Queries";
import { requirementDefinitionsApi } from "@api";
import { getRequirementsDependencies } from "../../modules/Requirements/dependencies";
import {
  QUERY_EXTENDED_REQUIREMENT_DEFINITION,
  QUERY_EXTENDED_REQUIREMENT_DEFINITIONS
} from "../../query/requirementDefinitions/keys";
import { QUERY_PAYEE_ROWS } from "../../query/search/payee/queries/useQueryPayeeRows";
import { QUERY_PAYEE_ROWS_SUMMARY } from "../../query/search/payee/queries/useQueryPayeeRowsSummary";

/**
 * Requirement-definition (template) registry — mirrors `RequirementDefinitionsApi`
 * 1:1. The name-derived defaults already refresh the sibling template reads
 * (`getRequirementDefinition` / `listRequirementDefinitions`) after every write,
 * so the configs below only add the cross-resource invalidations the retired v1
 * hooks declared:
 *  - the "extended" definition reads, which join a v3 template with its v3
 *    data-source configuration (signed-document template / shared-file request
 *    configuration / background-check configuration / insurance monitor
 *    configuration). Those are derived `useWSQuery`s under their own keys, so
 *    they live outside this registry's cache space,
 *  - `REQUIREMENTS_DEPENDENCIES` — every requirement *instance* read that a
 *    template change propagates into (portal lists, engagement widgets, payee
 *    rows, notifications, the audit-log feed),
 *  - the v1 payee-rows search keys (`QUERY_PAYEE_ROWS`/`QUERY_PAYEE_ROWS_SUMMARY`)
 *    the retired v1 update hook invalidated — the requirement columns on the
 *    still-v1 payee search screens reflect definition changes.
 *
 * `activate`/`deactivate` replace the v1 `PATCH { status }` archive/restore, so
 * they carry the same fan-out as `update`.
 *
 * The dependency lists are lazy (`() => [...]`) on purpose:
 * `REQUIREMENTS_DEPENDENCIES` is built from sibling registries' keys, so
 * keeping the reads at invalidation time decouples this registry's
 * construction from the other registries' module-init order.
 *
 * TODO(v3): drop `QUERY_EXTENDED_REQUIREMENT_DEFINITION(S)` once the join moves
 * into this registry — every data-source configuration it reads is v3 already,
 * so only the hand-rolled join key stands in the way.
 */
export const queriesRequirementDefinitions = createV3Queries(
  "requirementDefinitionsApi",
  requirementDefinitionsApi,
  {
    createRequirementDefinition: () => ({
      dependencies: () => [
        QUERY_EXTENDED_REQUIREMENT_DEFINITIONS,
        ...getRequirementsDependencies()
      ]
    }),
    updateRequirementDefinition: () => ({
      dependencies: () => [
        QUERY_EXTENDED_REQUIREMENT_DEFINITION,
        QUERY_EXTENDED_REQUIREMENT_DEFINITIONS,
        QUERY_PAYEE_ROWS,
        QUERY_PAYEE_ROWS_SUMMARY,
        ...getRequirementsDependencies()
      ]
    }),
    activateRequirementDefinition: () => ({
      dependencies: () => [
        QUERY_EXTENDED_REQUIREMENT_DEFINITION,
        QUERY_EXTENDED_REQUIREMENT_DEFINITIONS,
        QUERY_PAYEE_ROWS,
        QUERY_PAYEE_ROWS_SUMMARY,
        ...getRequirementsDependencies()
      ]
    }),
    deactivateRequirementDefinition: () => ({
      dependencies: () => [
        QUERY_EXTENDED_REQUIREMENT_DEFINITION,
        QUERY_EXTENDED_REQUIREMENT_DEFINITIONS,
        QUERY_PAYEE_ROWS,
        QUERY_PAYEE_ROWS_SUMMARY,
        ...getRequirementsDependencies()
      ]
    }),
    deleteRequirementDefinition: () => ({
      dependencies: () => [
        QUERY_EXTENDED_REQUIREMENT_DEFINITION,
        QUERY_EXTENDED_REQUIREMENT_DEFINITIONS
      ]
    })
  }
);
